/**
 * A source bundle's flat paths as a folder tree (RFC 0279 3d, S10).
 *
 * Pure, so it runs without React. Folders sort before files, each by name, the
 * way a repository lists them.
 */

export type SourceNode =
  | { kind: 'folder'; name: string; path: string; children: SourceNode[] }
  | { kind: 'file'; name: string; path: string }

type Folder = Extract<SourceNode, { kind: 'folder' }>

export function buildSourceTree(paths: readonly string[]): SourceNode[] {
  const root: Folder = { kind: 'folder', name: '', path: '', children: [] }
  for (const full of paths) {
    const parts = full.split('/').filter(Boolean)
    let at = root
    for (let i = 0; i < parts.length - 1; i++) {
      const path = parts.slice(0, i + 1).join('/')
      let next = at.children.find(
        (n): n is Folder => n.kind === 'folder' && n.path === path,
      )
      if (!next) {
        next = { kind: 'folder', name: parts[i], path, children: [] }
        at.children.push(next)
      }
      at = next
    }
    const name = parts[parts.length - 1]
    if (name) at.children.push({ kind: 'file', name, path: parts.join('/') })
  }
  sortTree(root.children)
  return root.children
}

function sortTree(nodes: SourceNode[]): void {
  nodes.sort((a, b) =>
    a.kind === b.kind
      ? a.name.localeCompare(b.name)
      : a.kind === 'folder'
        ? -1
        : 1,
  )
  for (const n of nodes) if (n.kind === 'folder') sortTree(n.children)
}

/** The file a Source tab opens on: the one a reader starts from. */
export function firstSourceFile(paths: readonly string[]): string | null {
  // The shallowest match: a root README before one in a vendored folder.
  const byName = (name: string) =>
    paths
      .filter((p) => p.split('/').pop()?.toLowerCase() === name)
      .sort((a, b) => a.split('/').length - b.split('/').length)[0]
  return (
    byName('readme.md') ??
    byName('package.json') ??
    byName('skill.md') ??
    paths[0] ??
    null
  )
}
